import { useEffect, useState } from 'react'
import { ChevronRight } from '../../../components/ChevronRight'
import { ScreenHeader } from '../../../components/ScreenHeader'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import { useAuth } from '../../../lib/auth'
import {
  daysUntilNextMonday, ensureMyColorfulWeeklyEntry,
  fetchColorfulWeeklyWinners,
  getOrCreateColorfulWeekly, thisMondayISO, weekLabel,
  type ColorfulWeeklyEntryRow, type ColorfulWeeklyEventRow, type ColorfulWeeklyWinnerRow,
} from '../api/weekly'
import { currentWeekStarts } from '../../../lib/api/weekly'
import { readError } from '../../../lib/errors'
import { WeeklyTipOverlay } from '../../../platform/weekly/WeeklyTipOverlay'
import { useWeeklyTip } from '../../../lib/api/userFlags'
import { useT } from '../../../i18n'

interface Props {
  onPlayThisWeek: (eventId: string, entryId: string) => void
  onBack: () => void
  /** Open the flat archive of every past Colorful week. */
  onOpenArchive: () => void
}

/**
 * Colorful Weekly hub. Same shape as the IET WeeklyHubScreen:
 *   - This Week card — the six subjects, a Play button, entrant count.
 *   - Last Week card — leaderboard link once the previous event is
 *     finalized (happens automatically the first time someone loads
 *     the hub in the new week).
 *   - Hall of Fame — all finalized weeks, top scorer per week.
 */
export function ColorfulWeeklyHubScreen({
  onPlayThisWeek, onBack, onOpenArchive,
}: Props) {
  const t = useT()
  const { profile, session, configured } = useAuth()
  const signedIn = Boolean(session && profile)

  const [thisWeek, setThisWeek] = useState<ColorfulWeeklyEventRow | null>(null)
  const [myEntry, setMyEntry] = useState<ColorfulWeeklyEntryRow | null>(null)
  const [winners, setWinners] = useState<ColorfulWeeklyWinnerRow[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { showTip, dismissTip } = useWeeklyTip('colorful')

  useEffect(() => {
    if (!configured) { setLoading(false); return }
    let cancelled = false
    async function load() {
      try {
        const weekStart = thisMondayISO()
        // get_or_create is also the thing that finalizes last week —
        // run it first so the rest of the fetches see the fresh state.
        const event = await getOrCreateColorfulWeekly(weekStart)
        if (cancelled) return
        setThisWeek(event)

        // Exclude the CURRENT VOTE WEEK from the winners list — it's
        // already shown on the Hub's "Last week · Vote" card and on
        // the dedicated Colorful Archive screen with the same exclude.
        // Excluding `weekStart` (this-week) instead would leak the
        // vote week into the Hub's archive count, then the Archive
        // screen would correctly hide it, leaving "1 past week" on
        // the Hub but an empty Archive — exactly the bug Jordan saw.
        const { lastWeek } = currentWeekStarts()
        const [entry, pastWinners] = await Promise.all([
          signedIn ? ensureMyColorfulWeeklyEntry(event.id) : Promise.resolve(null),
          fetchColorfulWeeklyWinners(lastWeek),
        ])
        if (cancelled) return
        setMyEntry(entry)
        setWinners(pastWinners)
      } catch (e) {
        if (!cancelled) setError(readError(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [configured, signedIn, profile?.id])

  if (!configured) {
    return (
      <Shell onBack={onBack}>
        <div className="card p-5 mx-5">
          <p className="font-semibold text-brand-text">{t("Supabase isn't configured")}</p>
          <p className="text-sm text-brand-muted mt-1">
            {t('The weekly event needs the backend to be set up.')}
          </p>
        </div>
      </Shell>
    )
  }

  if (loading || !thisWeek) {
    return (
      <Shell onBack={onBack}>
        <div className="px-5 space-y-3">
          <div className="h-40 rounded-2xl bg-brand-elevated animate-pulse" />
          <div className="h-20 rounded-2xl bg-brand-elevated animate-pulse" />
          <div className="h-20 rounded-2xl bg-brand-elevated animate-pulse" />
        </div>
      </Shell>
    )
  }

  const answeredCount = myEntry ? myEntry.picks.filter(Boolean).length : 0
  const isDone = answeredCount === 6

  return (
    <Shell onBack={onBack}>
      <div className="px-5 space-y-5 overflow-y-auto pb-6">
        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}

        {/* This week — play phase. Colorful palette (cream + tan
            stroke) so the weekly screens feel like the rest of the
            game, not the IET event. */}
        <div className="bg-game-colorful-light border-2 border-game-colorful-dark
                        rounded-2xl shadow-card p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide mb-1"
                 style={{ color: '#B8353C' }}>
                {t('This Week · Play')}
              </p>
              <h2 className="font-display text-xl font-bold text-brand-text">
                {weekLabel(thisWeek.week_start)}
              </h2>
            </div>
            <div className="countdown-pill">
              {t('{n}d left', { n: daysUntilNextMonday(thisWeek.week_start) })}
            </div>
          </div>

          <div className="mb-4">
            <p className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
              {t("This week's subjects")}
            </p>
            <ul className="space-y-1.5">
              {thisWeek.subjects.map((s, i) => {
                const picked = myEntry?.picks[i]
                return (
                  <li key={i} className="flex items-center gap-3 text-sm">
                    <span className="font-display font-bold w-5 text-brand-muted">
                      {i + 1}
                    </span>
                    <span className="flex-1 text-brand-text">{s}</span>
                    {picked && (
                      <span className="text-xs font-semibold text-brand-muted uppercase tracking-wide">
                        {picked}
                      </span>
                    )}
                  </li>
                )
              })}
            </ul>
          </div>

          {signedIn ? (
            <button
              onClick={() => myEntry && onPlayThisWeek(thisWeek.id, myEntry.id)}
              disabled={!myEntry}
              className={isDone ? 'btn-colorful-outline' : 'btn-colorful animate-pulse-glow'}
            >
              {isDone
                ? t('Edit your picks')
                : answeredCount > 0
                  ? t('Continue ({n}/6)', { n: answeredCount })
                  : t('Pick colors')}
            </button>
          ) : (
            <p className="text-sm text-brand-muted text-center">
              {t('Sign in to submit picks. Guests can read, not play.')}
            </p>
          )}
        </div>

        {/* Weekly archive — only render when there's at least one
            truly-past week. The current vote week is excluded from
            the archive, so until weeks accumulate this is empty and
            the button would tap into a "no results" page. */}
        {winners && winners.length > 0 && (
          <button
            onClick={onOpenArchive}
            className="w-full bg-game-colorful-light border-2 border-game-colorful-dark
                       rounded-2xl shadow-card p-4 flex items-center gap-3 text-left
                       active:scale-[0.98] transition-transform"
          >
            <div className="flex-1">
              <p className="font-semibold text-brand-text">{t('Weekly archive')}</p>
              <p className="text-xs text-brand-muted">
                {winners.length === 1
                  ? t('1 past week')
                  : t('{n} past weeks', { n: winners.length })}
              </p>
            </div>
            <ChevronRight color="#74705F" />
          </button>
        )}
      </div>

      {showTip && <WeeklyTipOverlay game="colorful" onDismiss={dismissTip} />}
    </Shell>
  )
}

function Shell({
  onBack, children,
}: {
  onBack: () => void
  children: React.ReactNode
}) {
  const t = useT()
  return (
    <div className="screen pb-24">
      <BackgroundTitle text={t('Weekly Colorful')} color="#74705F" />
      <ScreenHeader
        image="/games/colorful.webp"
        imageAlt="Colorful"
        leftSlot={
          <button
            onClick={onBack}
            className="w-9 h-9 flex items-center justify-center rounded-full
                       bg-brand-elevated active:bg-brand-border transition-colors"
            aria-label={t('Back to weekly hub')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M15 18l-6-6 6-6" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        }
      />
      <div className="relative z-10">{children}</div>
    </div>
  )
}
